import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AToggle, FormRow } from "@/components/admin/form";
import { CmsToolbar } from "@/components/admin/cms/cms-kit";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import { siteApi, type SitePromises } from "@/lib/site/site-api";

const inp: React.CSSProperties = {
	width: "100%",
	padding: "8px 10px",
	border: "1px solid var(--a-border)",
	borderRadius: 6,
	fontSize: 13,
	background: "var(--a-bg)",
	color: "var(--a-ink)",
	outline: "none",
};

function fcfa(n: number): string {
	return `${n.toLocaleString("fr-FR").replace(/,/g, " ")} FCFA`;
}

/**
 * Réglage des engagements commerciaux.
 *
 * Un seul endroit pour tout ce que le site promet. Ces valeurs étaient
 * auparavant réécrites dans chaque libellé, et avaient fini par se contredire :
 * la page d'accueil annonçait un retour sous sept jours quand la fiche produit
 * et les conditions générales en promettaient quatorze.
 *
 * Le seuil de livraison offerte mérite une attention particulière : il ne sert
 * pas qu'à l'affichage, il déclenche la gratuité au calcul des frais. Le
 * modifier change ce que les clients paieront réellement.
 */
export function SitePromisesEditor() {
	const qc = useQueryClient();
	const toast = useToast();
	// La page CMS est aussi ouverte aux réglages système, mais l'écriture des
	// engagements exige la permission éditoriale. Sans elle, on montre les
	// valeurs en lecture plutôt que de laisser saisir puis échouer.
	const { hasAny, isSuperAdmin } = usePermissions();
	const peutEcrire = isSuperAdmin || hasAny("MARKETING.CMS.WRITE");
	const { data, isLoading } = useQuery({
		queryKey: ["site-promises"],
		queryFn: siteApi.getPromises,
	});

	const [f, setF] = useState<Partial<SitePromises>>({});
	const [cities, setCities] = useState("");
	const [methods, setMethods] = useState("");

	useEffect(() => {
		if (!data) return;
		setF(data);
		setCities((data.mainCities ?? []).join(", "));
		setMethods((data.paymentMethods ?? []).join(", "));
		setDirty(false);
	}, [data]);

	// Signalé seulement après une vraie saisie : marquer dès le chargement
	// ferait clignoter l'avertissement à chaque ouverture de l'écran.
	const [dirty, setDirty] = useState(false);
	const set = (patch: Partial<SitePromises>) => {
		setDirty(true);
		setF((prev) => ({ ...prev, ...patch }));
	};

	// Les bornes sont celles que l'API accepte : les refuser ici évite un
	// aller-retour pour une saisie que le serveur rejettera de toute façon.
	const nb = (v: string, min: number, max: number, fallback: number) => {
		const n = Number.parseInt(v, 10);
		if (Number.isNaN(n)) return fallback;
		return Math.min(max, Math.max(min, n));
	};

	const invalide =
		(f.etaMainMinHours ?? 0) > (f.etaMainMaxHours ?? 0)
			? "Le délai le plus court dépasse le plus long."
			: null;

	const save = useMutation({
		mutationFn: () =>
			siteApi.updatePromises({
				returnDays: f.returnDays,
				returnFree: f.returnFree,
				freeShippingEnabled: f.freeShippingEnabled,
				freeShippingThreshold: f.freeShippingThreshold,
				etaMainMinHours: f.etaMainMinHours,
				etaMainMaxHours: f.etaMainMaxHours,
				warrantyMonths: f.warrantyMonths,
				cashOnDelivery: f.cashOnDelivery,
				mainCities: cities
					.split(",")
					.map((c) => c.trim())
					.filter(Boolean),
				paymentMethods: methods
					.split(",")
					.map((m) => m.trim())
					.filter(Boolean),
			}),
		onSuccess: (res) => {
			qc.setQueryData(["site-promises"], res);
			setDirty(false);
			toast.success("Engagements mis à jour sur tout le site.");
		},
		onError: (e) =>
			toast.error(extractErrorMessage(e, "Enregistrement impossible")),
	});

	if (isLoading) return <Card>
		<CardBody>
			<span className="muted">Chargement…</span>
		</CardBody>
	</Card>;

	return (
		<>
			<CmsToolbar
				title="Engagements commerciaux"
				subtitle={
					peutEcrire
						? "Ce que le site promet — repris automatiquement sur toutes les pages."
						: "Lecture seule : la permission « CMS » est requise pour modifier."
				}
				dirty={dirty}
				saving={save.isPending}
				onSave={peutEcrire && !invalide ? () => save.mutate() : undefined}
				saveLabel="Enregistrer les engagements"
			/>

		<div className="grid-2" style={{ alignItems: "start" }}>
			<Card>
				<CardHead title="Retour & garantie" />
				<CardBody>
					<FormRow
						label="Délai de rétractation"
						helper="affiché partout où l'on parle de retour"
					>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<input
								type="number"
								min={1}
								max={60}
								style={{ ...inp, width: 90 }}
								disabled={!peutEcrire}
								value={f.returnDays ?? 14}
								onChange={(e) =>
									set({ returnDays: nb(e.target.value, 1, 60, 14) })
								}
							/>
							<span className="muted" style={{ fontSize: 12 }}>
								jours
							</span>
						</div>
					</FormRow>

					<FormRow label="Retour sans frais pour le client">
						<AToggle
							on={f.returnFree ?? true}
							onChange={(v) => peutEcrire && set({ returnFree: v })}
						/>
					</FormRow>

					<FormRow
						label="Garantie constructeur"
						helper="0 pour ne rien annoncer"
					>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<input
								type="number"
								min={0}
								max={120}
								style={{ ...inp, width: 90 }}
								disabled={!peutEcrire}
								value={f.warrantyMonths ?? 12}
								onChange={(e) =>
									set({ warrantyMonths: nb(e.target.value, 0, 120, 12) })
								}
							/>
							<span className="muted" style={{ fontSize: 12 }}>
								mois
							</span>
						</div>
					</FormRow>

					<FormRow label="Moyens de paiement annoncés" helper="séparés par des virgules">
						<input
							style={inp}
							disabled={!peutEcrire}
							value={methods}
							onChange={(e) => {
								setDirty(true);
								setMethods(e.target.value);
							}}
						/>
					</FormRow>

					<FormRow label="Paiement à la livraison proposé">
						<AToggle
							on={f.cashOnDelivery ?? true}
							onChange={(v) => peutEcrire && set({ cashOnDelivery: v })}
						/>
					</FormRow>
				</CardBody>
			</Card>

			<Card>
				<CardHead title="Livraison" />
				<CardBody>
					<FormRow label="Livraison offerte au-delà d'un montant">
						<AToggle
							on={f.freeShippingEnabled ?? true}
							onChange={(v) => peutEcrire && set({ freeShippingEnabled: v })}
						/>
					</FormRow>

					<FormRow
						label="Seuil de livraison offerte"
						helper="ce montant est aussi appliqué au calcul des frais"
					>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<input
								type="number"
								min={0}
								max={10_000_000}
								step={500}
								disabled={!peutEcrire || !(f.freeShippingEnabled ?? true)}
								style={{
									...inp,
									width: 140,
									opacity: (f.freeShippingEnabled ?? true) ? 1 : 0.5,
								}}
								value={f.freeShippingThreshold ?? 25_000}
								onChange={(e) =>
									set({
										freeShippingThreshold: nb(
											e.target.value,
											0,
											10_000_000,
											25_000,
										),
									})
								}
							/>
							<span className="muted" style={{ fontSize: 12 }}>
								FCFA
							</span>
						</div>
					</FormRow>

					<div
						style={{
							background: "var(--a-bg-2)",
							border: "1px solid var(--a-border)",
							borderRadius: 8,
							padding: "10px 12px",
							marginBottom: 14,
							fontSize: 12,
						}}
					>
						{(f.freeShippingEnabled ?? true) ? (
							<>
								Une commande de{" "}
								<strong>{fcfa(f.freeShippingThreshold ?? 25_000)}</strong> ou plus
								sera livrée sans frais. En dessous, les frais de la zone
								s'appliquent.
							</>
						) : (
							<>
								Aucune livraison offerte : les frais de la zone s'appliquent à
								toutes les commandes, et aucun seuil n'est annoncé sur le site.
							</>
						)}
					</div>

					<FormRow
						label="Délai annoncé pour les villes principales"
						helper="en heures"
					>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<input
								type="number"
								min={1}
								max={168}
								style={{ ...inp, width: 80 }}
								disabled={!peutEcrire}
								value={f.etaMainMinHours ?? 24}
								onChange={(e) =>
									set({ etaMainMinHours: nb(e.target.value, 1, 168, 24) })
								}
							/>
							<span className="muted" style={{ fontSize: 12 }}>
								à
							</span>
							<input
								type="number"
								min={1}
								max={336}
								style={{ ...inp, width: 80 }}
								disabled={!peutEcrire}
								value={f.etaMainMaxHours ?? 48}
								onChange={(e) =>
									set({ etaMainMaxHours: nb(e.target.value, 1, 336, 48) })
								}
							/>
							<span className="muted" style={{ fontSize: 12 }}>
								heures
							</span>
						</div>
					</FormRow>
					{invalide && (
						<div
							style={{
								color: "var(--a-danger)",
								fontSize: 12,
								marginTop: -8,
								marginBottom: 12,
							}}
						>
							{invalide}
						</div>
					)}

					<FormRow
						label="Villes couvertes par ce délai"
						helper="séparées par des virgules"
					>
						<input
							style={inp}
							disabled={!peutEcrire}
							value={cities}
							onChange={(e) => {
								setDirty(true);
								setCities(e.target.value);
							}}
						/>
					</FormRow>
				</CardBody>
			</Card>

		</div>
		</>
	);
}
